/**
 * Collections V8 — card purchase-options panel behavior.
 *
 * Desktop (hover-capable): the panel reveal is pure CSS (:hover / :focus-within
 * on .clv8-card__cta). This script only handles:
 *   1. Touch devices: first tap on the ATC opens the panel (RŌZ pattern)
 *      instead of adding to cart; the second tap submits normally.
 *   2. Closing open panels on outside tap / after an add.
 *   3. Keeping the panel's price rows in sync after a size swap
 *      (VariantSelectCard rewrites data-base-price / data-sub-price on the
 *      <product-purchase-options> element; we mirror those into the panel).
 *
 * The subscribe/one-time mechanics themselves (selling_plan toggling, ATC
 * price rewrite, persistence) are handled by the shared
 * <product-purchase-options> element in component-product.js — untouched here.
 */
(function () {
  'use strict';

  function closeOpenPanels(except) {
    document
      .querySelectorAll('card-product.clv8-panel-open')
      .forEach(function (card) {
        if (card !== except) card.classList.remove('clv8-panel-open');
      });
  }

  document.addEventListener('click', function (event) {
    var btn = event.target.closest('.clv8-card__cta button[name="add"]');
    var card = event.target.closest('card-product.clv8-card');

    // Outside click closes any open panel
    closeOpenPanels(card);

    if (!btn) return;

    var owner = btn.closest('card-product.clv8-card');
    var panel = owner && owner.querySelector('.clv8-panel');
    if (!panel) return;

    // Decide per click from the panel's ACTUAL visibility, not a load-time
    // hover-capability snapshot (which broke DevTools mobile emulation
    // toggled after load). Desktop mouse users reach the button through
    // :hover, so the panel is already visible and the click adds to cart.
    // Anywhere hover CSS isn't active (real touch, emulated touch), the
    // panel is hidden — so the first click reveals it instead of adding.
    var panelVisible =
      owner.classList.contains('clv8-panel-open') ||
      getComputedStyle(panel).visibility === 'visible';

    if (!panelVisible) {
      // First tap: open the options panel instead of adding to cart
      event.preventDefault();
      owner.classList.add('clv8-panel-open');
    }
    // Otherwise fall through: the form submits and the shared cart-drawer
    // submit handler adds to cart (with selling_plan when Subscribe is on).
  });

  // After a successful submit, collapse the panel
  document.addEventListener('submit', function (event) {
    var card =
      event.target && event.target.closest
        ? event.target.closest('card-product.clv8-card')
        : null;
    if (card) card.classList.remove('clv8-panel-open');
  });

  /* ------------------------------------------------------------------
     Panel price sync on size swap.
     fetchVariantData() updates data-base-price / data-sub-price /
     data-discount-percent on <product-purchase-options> but only rewrites
     the ATC price span. Observe those attributes and refresh the panel's
     own price rows so they never go stale.
     ------------------------------------------------------------------ */
  function formatMoney(cents) {
    if (window.CurrencyUtils && window.CurrencyUtils.formatMoney) {
      return window.CurrencyUtils.formatMoney(cents);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function refreshPanelPrices(po) {
    var panel = po.closest('.clv8-panel');
    if (!panel) return;

    var base = Number(po.getAttribute('data-base-price')) || 0;
    var sub = Number(po.getAttribute('data-sub-price')) || 0;
    if (base <= 0) return;

    var subPriceEl = panel.querySelector('[data-clv8-sub-price]');
    if (subPriceEl) {
      if (sub > 0 && sub < base) {
        subPriceEl.innerHTML =
          '<span class="clv8-compare">' +
          formatMoney(base) +
          '</span>' +
          formatMoney(sub);
      } else {
        subPriceEl.textContent = formatMoney(base);
      }
    }

    var oneTimeEl = panel.querySelector('[data-clv8-onetime-price]');
    if (oneTimeEl) {
      oneTimeEl.textContent = formatMoney(base);
    }
  }

  /* ------------------------------------------------------------------
     Custom frequency dropdown.
     The native <select> popup is browser chrome: it can't be styled and
     mispositions inside DevTools mobile emulation / transformed ancestors
     ("options floating off in space"). Replace it with an in-page listbox
     anchored to the field. The <select> stays in the DOM (hidden) so the
     shared subscription JS and form submission keep working — we just
     proxy value changes through it.
     ------------------------------------------------------------------ */
  function closeDropdowns(except) {
    document.querySelectorAll('.clv8-dd.is-open').forEach(function (dd) {
      if (dd === except) return;
      dd.classList.remove('is-open');
      var t = dd.querySelector('.clv8-dd__toggle');
      if (t) t.setAttribute('aria-expanded', 'false');
      var panel = dd.closest('.clv8-panel');
      if (panel) panel.classList.remove('is-dd-open');
    });
  }

  function enhanceSelect(po) {
    var select = po.querySelector('select[data-subscription-select]');
    if (!select || select.dataset.clv8Enhanced) return;
    select.dataset.clv8Enhanced = '1';

    var wrap = document.createElement('div');
    wrap.className = 'clv8-dd';
    select.parentNode.insertBefore(wrap, select);
    wrap.appendChild(select);

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'clv8-dd__toggle';
    btn.setAttribute('aria-haspopup', 'listbox');
    btn.setAttribute('aria-expanded', 'false');
    btn.innerHTML =
      '<span class="clv8-dd__label"></span>' +
      '<svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
      '<path d="m2 4 4 4 4-4" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    var menu = document.createElement('div');
    menu.className = 'clv8-dd__menu';
    menu.setAttribute('role', 'listbox');
    wrap.appendChild(btn);
    wrap.appendChild(menu);

    var label = btn.querySelector('.clv8-dd__label');

    function sync() {
      var opt = select.options[select.selectedIndex];
      label.textContent = opt ? opt.textContent.trim() : '';
    }

    function close() {
      wrap.classList.remove('is-open');
      btn.setAttribute('aria-expanded', 'false');
      var panel = wrap.closest('.clv8-panel');
      if (panel) panel.classList.remove('is-dd-open');
    }

    function build() {
      menu.innerHTML = '';
      Array.prototype.forEach.call(select.options, function (opt) {
        var item = document.createElement('button');
        item.type = 'button';
        item.className =
          'clv8-dd__option' + (opt.selected ? ' is-selected' : '');
        item.setAttribute('role', 'option');
        if (opt.selected) item.setAttribute('aria-selected', 'true');
        item.textContent = opt.textContent.trim();
        item.addEventListener('click', function () {
          select.value = opt.value;
          // fires the shared subscription JS (selling_plan sync) and the
          // subscribe-intent radio switch below
          select.dispatchEvent(new Event('change', { bubbles: true }));
          sync();
          close();
        });
        menu.appendChild(item);
      });
    }

    btn.addEventListener('click', function () {
      if (wrap.classList.contains('is-open')) {
        close();
      } else {
        closeDropdowns(wrap);
        build(); // rebuilt each open — survives option repopulation on size swaps
        wrap.classList.add('is-open');
        btn.setAttribute('aria-expanded', 'true');
        // Lift the panel above the size selector (z-index 6) while open so
        // the menu isn't clipped behind it.
        var panel = wrap.closest('.clv8-panel');
        if (panel) panel.classList.add('is-dd-open');
      }
    });

    select.addEventListener('change', sync);
    sync();
  }

  document.addEventListener('click', function (event) {
    if (!event.target.closest('.clv8-dd')) closeDropdowns(null);
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') closeDropdowns(null);
  });

  /* ------------------------------------------------------------------
     ATC label: "Add to bag" ↔ "Subscribe" following the purchase-type
     radios. Synced on init (the shared JS restores a persisted choice
     without firing change events) and on every radio change.
     ------------------------------------------------------------------ */
  function syncAtcLabel(po) {
    var card = po.closest('card-product.clv8-card');
    var label = card && card.querySelector('[data-clv8-atc-label]');
    if (!label) return;
    var subRadio = po.querySelector(
      '[name="purchase_type"][value="subscription"]'
    );
    var subscribed = !!(subRadio && subRadio.checked);
    var next = subscribed
      ? label.getAttribute('data-label-subscribe') || 'Subscribe'
      : label.getAttribute('data-label-default') || 'Add to bag';
    // Only write when the text actually changes: setting textContent replaces
    // the text node even for an identical string, which counts as a DOM
    // mutation — and this label lives inside the subtree our grid
    // MutationObserver watches. An unconditional write re-triggers the
    // observer in an infinite feedback loop that freezes the tab.
    if (label.textContent.trim() !== next.trim()) {
      label.textContent = next;
    }
  }

  document.addEventListener('change', function (event) {
    if (!event.target.matches || !event.target.matches('[data-purchase-type-radio]')) return;
    var po = event.target.closest('product-purchase-options');
    if (po && po.closest('card-product.clv8-card')) syncAtcLabel(po);
  });

  function observePurchaseOptions(root) {
    root
      .querySelectorAll('card-product.clv8-card product-purchase-options')
      .forEach(function (po) {
        enhanceSelect(po);
        if (po.dataset.clv8PriceObserved) return;
        po.dataset.clv8PriceObserved = '1';
        // One-time initial label sync per element, after the shared
        // <product-purchase-options> init (which may restore a persisted
        // subscribe choice without firing change events) has had a beat.
        // MUST stay behind the once-guard above — scheduling this on every
        // observer pass created a mutation feedback storm.
        setTimeout(function () {
          syncAtcLabel(po);
        }, 50);
        new MutationObserver(function () {
          refreshPanelPrices(po);
        }).observe(po, {
          attributes: true,
          attributeFilter: ['data-base-price', 'data-sub-price']
        });

        // The frequency dropdown is always visible (even with One Time
        // selected). If the user changes it, that's a subscribe intent —
        // switch the radio so the selection can't desync from the UI.
        var select = po.querySelector('select[data-subscription-select]');
        if (select) {
          select.addEventListener('change', function () {
            var subRadio = po.querySelector(
              '[name="purchase_type"][value="subscription"]'
            );
            if (subRadio && !subRadio.checked) {
              subRadio.checked = true;
              subRadio.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });
        }
      });
  }

  function init() {
    observePurchaseOptions(document);

    // Cards appended later (load more / AJAX filtering) need observers too.
    // Coalesce bursts of mutations into one pass per frame — the callback's
    // own DOM work (select enhancement, label sync) mutates the watched
    // subtree, so running it once per mutation record would feed back.
    var grid = document.getElementById('product-grid');
    var container = document.getElementById('ProductGridContainer');
    var watchTarget = container || grid;
    if (watchTarget) {
      var pending = false;
      new MutationObserver(function () {
        if (pending) return;
        pending = true;
        requestAnimationFrame(function () {
          pending = false;
          observePurchaseOptions(document);
        });
      }).observe(watchTarget, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
